import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Card,
  FormHelperText,
  Tab,
  Box,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LoadingButton, TabContext, TabPanel, TabList } from "@mui/lab";
import { useFormik, Form, FormikProvider, FieldArray } from "formik";
import * as Yup from "yup";
import { useMutate } from "src/hooks/useFetch.hook";
import { Link as RouterLink, useParams, useNavigate, useLocation } from "react-router-dom";
import { Loader } from "../Loader";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import { fileUpload } from "../ServiceRequest/serviceRequest.hook";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  addVehicleUrl,
  deleteVehicleUrl,
  getVehicleByVehicleId,
  updateVehicleUrl,
} from "src/react-query/endPoints";
import { queryKeys } from "src/react-query/queryKeys";
import { useSelectedCompany, useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useDispatch } from "react-redux";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { FormikDateTimePicker } from "../FormikDateTimePicker";
import { VehicleChargingHistory } from "./VehicleChargingHistory";
import { canonicalMac, formatMac, isValidMac } from "src/utils/macAddress";


const VehicleDetailsForm = ({ onVehicleLoad }) => {
  const dispatch = useDispatch();
  const { vehicleId: id } = useParams(); // Customer ID (user_pk)
  const { teamId } = useSelectedTeam();
  const company = useSelectedCompany();
  const navigate = useNavigate();
  // Opened from the dashboard: go back there (Cancel / after save), else the list.
  const location = useLocation();
  const backPath = location.state?.returnTo || "/vehicles";
  const { mutate, isLoading } = useMutate("vehicle");
  // const { data: vehicleData, isFetching } = useGetDetail("vehicles", id);
  const [uploading, setUploading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [selectedTab, setSelectedTab] = useState("details");
  const fileUploadUrl = useMutation(fileUpload);
  const {
    data: vehicleData,
    isFetching,
    refetch,
  } = useQuery(
    [queryKeys.vehicles, teamId, id],
    () => getVehicleByVehicleId(teamId, id),
    {
      enabled: !!id && !!teamId, // Only fetch if both id and teamId are available
      onSuccess: (data) => {
        setVehicles(data?.data || []);
        formik.setValues({ vehicles: data?.data || [] });
        const regNum = data?.data?.[0]?.registration_number;
        if (regNum && onVehicleLoad) onVehicleLoad(regNum);
      },
      onError: (error) => {
        console.error('VehicleDetailForm: Error fetching vehicle details:', {
          error: error.message,
          stack: error.stack,
          vehicleId: id,
          teamId,
        });
        if (
          error.message === "No token provided." ||
          error.message === "Invalid token."
        ) {
          // Redirect to login page if token is missing or invalid
          navigate("/login");
        }
      },
    }
  );

  // 🚀 File Upload Handler (Upload to S3)
  const handleFileUpload = async (event, field, index) => {
    const file = event.target.files[0];
    if (!file || file.type !== "application/pdf") {
      return alert("Only PDF files are allowed!");
    }

    setUploading(true);

    try {
      // Convert the file to base64
      const fileInBase64 = await fileToBase64(file);

      // Use the mutate method to upload the file
      fileUploadUrl.mutate(
        {
          image: fileInBase64,
          directory: "vehicle-documents", // Specify the directory in S3
        },
        {
          onSuccess: (response) => {
            // Update the Formik field with the uploaded file URL
            formik.setFieldValue(
              `vehicles[${index}].${field}`,
              response.data.data
            );
            setUploading(false);
          },
          onError: (error) => {
            console.error("File Upload Error:", error);
            setUploading(false);
          },
        }
      );
    } catch (error) {
      console.error("Error converting file to base64:", error);
      setUploading(false);
    }
  };

  const onSubmit = async (values, { setSubmitting }) => {
    // Clone to avoid mutating Formik state
    const vehicle = { ...values.vehicles[0] };

    // Battery MACs go to the API as battery_macs: [battery 1, battery 2] (either may be
    // empty). The API stores them canonical; the form shows them as AA:BB:CC:DD:EE:FF.
    vehicle.battery_macs = [vehicle.battery_mac_1 || "", vehicle.battery_mac_2 || ""];
    delete vehicle.battery_mac_1;
    delete vehicle.battery_mac_2;

    // Safely format local date objects back to YYYY-MM-DD string for the backend
    const dateFields = ["insurance_expiry_date", "last_service_date", "next_service_date", "emi_start_date", "emi_end_date"];
    dateFields.forEach(field => {
      const d = vehicle[field];
      if (d && d instanceof Date && !isNaN(d)) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        vehicle[field] = `${y}-${m}-${day}`;
      }
    });
    
    const isUpdate = !!id;
    await mutate(
      {
        url: isUpdate
          ? updateVehicleUrl(company.id, id)
          : addVehicleUrl(company.id),
        method: isUpdate ? "PUT" : "POST",
        body: vehicle,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => {
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "",
        tabOpened: "",
      })
    );
    navigate(backPath);
  };

  // 🚀 Formik Form
  const formik = useFormik({
    initialValues: {
      vehicles: vehicleData || [
        {
          id: "", // Vehicle ID (for update/delete)
          vehicle_type: "",
          make: "",
          model: "",
          variant: "",
          registration_number: "",
          chassis_number: "",
          engine_number: "",
          vin: "",
          battery_mac_1: "",
          battery_mac_2: "",
          license_plate_state: "",
          battery_capacity: "",
          motor_type: "",
          seating_capacity: "",
          color: "",
          body_type: "",
          last_service_date: "",
          service_history: "",
          next_service_date: "",
          warranty_details: "",
          roadside_assistance_provider: "",
          owner_name: "",
          owner_contact: "",
          usage_type: "",
          odometer_reading: "",
          vehicle_insaurance_provider: "",
          insaurance_policy_number: "",
          insaurance_expiry_date: "",
          total_financed_amount: "",
          emi_amount: "",
          emi_start_date: "",
          emi_end_date: "",
          emi_cycle: "",
          finance_company_name: "",
          key_features: "",
          range_per_charge: "",
          charging_port_type: "",
          top_speed: "",
          tire_details: "",
          registration_certificate: "",
          insaurance_document: "",
          pollution_certificate: "",
          invoice_document: "",
          loan_lease_details: "",
        },
      ],
    },
    validationSchema: Yup.object({
      vehicles: Yup.array().of(
        Yup.object({
          vehicle_type: Yup.string()
            .required("Vehicle Type is required")
            .nullable(false),
          make: Yup.string().required("Make is required"),
          model: Yup.string().required("Model is required"),
          // Optional; when given, 12 hex digits (any separators). Must differ from each other.
          battery_mac_1: Yup.string()
            .nullable()
            .test("mac-format", "Enter a valid MAC, e.g. 9C:F6:DD:92:5B:C3", (v) => !v || isValidMac(v)),
          battery_mac_2: Yup.string()
            .nullable()
            .test("mac-format", "Enter a valid MAC, e.g. 9C:F6:DD:92:5B:C3", (v) => !v || isValidMac(v))
            .test("mac-distinct", "Battery 2 MAC must differ from Battery 1", function (v) {
              return !v || canonicalMac(v) !== canonicalMac(this.parent.battery_mac_1);
            }),
          year_of_manufacture: Yup.number()
            .typeError("Year of Manufacture must be a number")
            .required("Year of Manufacture is required")
            .min(1900, "Year must be 1900 or later")
            .max(2100, "Year must be 2100 or earlier"),
          registration_number: Yup.string().required(
            "Registration Number is required"
          ),
          owner_name: Yup.string().required("Owner Name is required"),
          usage_type: Yup.string()
            .required("Usage Type is required")
            .nullable(false),
          battery_capacity: Yup.number()
            .typeError("Battery Capacity must be a valid number")
            .positive("Battery Capacity must be greater than 0")
            .nullable(),
          seating_capacity: Yup.number()
            .typeError("Seating Capacity must be a valid number")
            .integer("Seating Capacity must be a whole number")
            .positive("Seating Capacity must be greater than 0")
            .nullable(),
          odometer_reading: Yup.number()
            .typeError("Odometer Reading must be a valid number")
            .min(0, "Odometer Reading cannot be negative")
            .nullable(),
          top_speed: Yup.number()
            .typeError("Top Speed must be a valid number")
            .positive("Top Speed must be greater than 0")
            .nullable(),
          range_per_charge: Yup.number()
            .typeError("Range Per Charge must be a valid number")
            .positive("Range Per Charge must be greater than 0")
            .nullable(),
          total_financed_amount: Yup.number()
            .typeError("Total Financed Amount must be a valid number")
            .min(0, "Total Financed Amount cannot be negative")
            .nullable(),
          emi_amount: Yup.number()
            .typeError("EMI Amount must be a valid number")
            .min(0, "EMI Amount cannot be negative")
            .nullable(),
        })
      ),
    }),
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = formik;

  useEffect(() => {
    if (vehicleData) {
      const vehicles = vehicleData.data.map((vehicle) => ({
        id: vehicle.vehicle_id,
        user_pk: vehicle.user_pk,
        companyId: vehicle.companyId,
        vehicle_type: vehicle.vehicle_type || "",
        make: vehicle.make || "",
        model: vehicle.model || "",
        variant: vehicle.variant || "",
        year_of_manufacture: vehicle.year_of_manufacture || "",
        registration_number: vehicle.registration_number || "",
        chassis_number: vehicle.chassis_number || "",
        engine_number: vehicle.engine_number || "",
        vin: vehicle.vin || "",
        battery_mac_1: formatMac(vehicle.battery_mac_1),
        battery_mac_2: formatMac(vehicle.battery_mac_2),
        license_plate_state: vehicle.license_plate_state || "",
        battery_capacity: vehicle.battery_capacity || "",
        motor_type: vehicle.motor_type || "", // Replace null with empty string
        seating_capacity: vehicle.seating_capacity || "",
        color: vehicle.color || "",
        body_type: vehicle.body_type || "", // Replace null with empty string
        last_service_date: vehicle.last_service_date || "",
        service_history: vehicle.service_history || "",
        next_service_date: vehicle.next_service_date || "",
        warranty_details: vehicle.warranty_details || "",
        roadside_assistance_provider:
          vehicle.roadside_assistance_provider || "",
        owner_name: vehicle.owner_name || "",
        owner_contact: vehicle.owner_contact || "",
        usage_type: vehicle.usage_type || "",
        odometer_reading: vehicle.odometer_reading || "",
        vehicle_insurance_provider: vehicle.vehicle_insurance_provider || "",
        insurance_policy_number: vehicle.insurance_policy_number || "",
        insurance_expiry_date: vehicle.insurance_expiry_date || "",
        key_features: vehicle.key_features || "",
        range_per_charge: vehicle.range_per_charge || "",
        charging_port_type: vehicle.charging_port_type || "", // Replace null with empty string
        top_speed: vehicle.top_speed || "",
        tire_details: vehicle.tire_details || "",
        total_financed_amount: vehicle.total_financed_amount || "",
        emi_amount: vehicle.emi_amount || "",
        emi_start_date: vehicle.emi_start_date || "",
        emi_end_date: vehicle.emi_end_date || "",
        emi_cycle: vehicle.emi_cycle || "", // Replace null with empty string
        finance_company_name: vehicle.finance_company_name || "",
        registration_certificate: vehicle.registration_certificate || "",
        insurance_document: vehicle.insurance_document || "",
        pollution_certificate: vehicle.pollution_certificate || "",
        invoice_document: vehicle.invoice_document || "",
        loan_lease_details: vehicle.loan_lease_details || "",
      }));

      formik.setValues({
        vehicles,
      });
    }
  }, [vehicleData]);

  // 🚀 Save Vehicle Data
  //   const saveVehicle = async (vehicle) => {
  //     try {
  //       const response = await mutate({
  //         url: addVehicleUrl(teamId),
  //         method: "POST",
  //         body: vehicle,
  //       });

  //       if (response.data) {
  //         await refetch();
  //         alert("Vehicle saved successfully!");
  //         navigate("/vehicles");
  //       }
  //     } catch (error) {
  //       console.error("Error saving vehicle:", error);
  //     }
  //   };

  //   const updateVehicle = async (vehicle) => {
  //     if (!vehicle.id && !vehicle.vehicleId) {
  //       return alert("Vehicle ID is required for update.");
  //     }

  //     try {
  //       const response = await mutate({
  //         url: updateVehicleUrl(teamId, vehicle.id || vehicle.vehicleId),
  //         method: "PUT",
  //         body: vehicle,
  //       });

  //       if (response.data) {
  //         await refetch();
  //         alert("Vehicle updated successfully!");
  //       }
  //     } catch (error) {
  //       console.error("Error updating vehicle:", error);
  //     }
  //   };

  //   const removeVehicle = async (vehicleId) => {
  //     if (!vehicleId) {
  //       return alert("Vehicle ID is required for removal.");
  //     }

  //     try {
  //       const response = await mutate({
  //         url: deleteVehicleUrl(id, vehicleId),
  //         method: "DELETE",
  //       });

  //       if (response.data) {
  //         await refetch();
  //         alert("Vehicle removed successfully!");
  //       }
  //     } catch (error) {
  //       console.error("Error removing vehicle:", error);
  //     }
  //   };

  if (isFetching) return <Loader />;

  const formBody = (
    <Card sx={{ p: 3 }}>
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <FieldArray
            name="vehicles"
            render={({ push, remove }) => (
              <>
                {formik.values.vehicles.map((vehicle, index) => (
                  <Grid container spacing={3} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="h6">Vehicle Details</Typography>
                      <Divider />
                    </Grid>

                    {/* Vehicle Fields (unchanged) */}
                    {/* Vehicle Type */}
                    <Grid item xs={4}>
                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.vehicles?.[index]?.vehicle_type &&
                            errors.vehicles?.[index]?.vehicle_type
                        )}
                      >
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].vehicle_type`
                          )}
                          value={
                            formik.values.vehicles[index].vehicle_type || ""
                          }
                        >
                          <MenuItem value="" disabled>
                            Select Vehicle Type
                          </MenuItem>
                          <MenuItem value="Car">Car</MenuItem>
                          <MenuItem value="Bike">Bike</MenuItem>
                          <MenuItem value="3-Wheeler">3-Wheeler</MenuItem>
                          <MenuItem value="Truck">Truck</MenuItem>
                        </Select>
                        {touched.vehicles?.[index]?.vehicle_type &&
                          errors.vehicles?.[index]?.vehicle_type && (
                            <FormHelperText error>
                              {errors.vehicles[index].vehicle_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>
                    {/* Make */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Make"
                        {...formik.getFieldProps(`vehicles[${index}].make`)}
                        error={Boolean(
                          touched.vehicles?.[index]?.make &&
                            errors.vehicles?.[index]?.make
                        )}
                        helperText={
                          touched.vehicles?.[index]?.make &&
                          errors.vehicles?.[index]?.make
                        }
                      />
                    </Grid>
                    {/* Model */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Model"
                        {...formik.getFieldProps(`vehicles[${index}].model`)}
                        error={Boolean(
                          touched.vehicles?.[index]?.model &&
                            errors.vehicles?.[index]?.model
                        )}
                        helperText={
                          touched.vehicles?.[index]?.model &&
                          errors.vehicles?.[index]?.model
                        }
                      />
                    </Grid>
                    {/* Varriant */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Varriant"
                        {...formik.getFieldProps(`vehicles[${index}].variant`)}
                      />
                    </Grid>
                    {/* Year of Manufacture */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Year of Manufacture"
                        type="number"
                        inputProps={{ min: 1900, max: 2100 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].year_of_manufacture`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.year_of_manufacture &&
                            errors.vehicles?.[index]?.year_of_manufacture
                        )}
                        helperText={
                          touched.vehicles?.[index]?.year_of_manufacture &&
                          errors.vehicles?.[index]?.year_of_manufacture
                        }
                      />
                    </Grid>
                    {/* Registration Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Registration Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].registration_number`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.registration_number &&
                            errors.vehicles?.[index]?.registration_number
                        )}
                        helperText={
                          touched.vehicles?.[index]?.registration_number &&
                          errors.vehicles?.[index]?.registration_number
                        }
                      />
                    </Grid>
                    {/* Chassis Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Chassis Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].chassis_number`
                        )}
                      />
                    </Grid>
                    {/* Engine Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Engine Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].engine_number`
                        )}
                      />
                    </Grid>
                    {/* Vehicle Identification Number (a real VIN — battery MACs are below) */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Vehicle Identification Number (VIN)"
                        {...formik.getFieldProps(`vehicles[${index}].vin`)}
                      />
                    </Grid>
                    {/* Battery MAC addresses — how charging sessions are matched to this vehicle */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Battery MAC Addresses
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        The charger reports the battery&apos;s MAC when it starts charging; sessions
                        from these MACs appear in this vehicle&apos;s charging history and on the dashboard.
                      </Typography>
                    </Grid>
                    {[1, 2].map((slot) => {
                      const field = `battery_mac_${slot}`;
                      const fieldError =
                        touched.vehicles?.[index]?.[field] && errors.vehicles?.[index]?.[field];
                      return (
                        <Grid item xs={4} key={field}>
                          <TextField
                            fullWidth
                            label={`Battery ${slot} MAC`}
                            placeholder="9C:F6:DD:92:5B:C3"
                            {...formik.getFieldProps(`vehicles[${index}].${field}`)}
                            onBlur={(e) => {
                              // Tidy valid input to AA:BB:CC:DD:EE:FF on blur.
                              if (isValidMac(e.target.value)) {
                                formik.setFieldValue(`vehicles[${index}].${field}`, formatMac(e.target.value));
                              }
                              formik.handleBlur(e);
                            }}
                            error={Boolean(fieldError)}
                            helperText={fieldError || "Optional — 12 hex digits"}
                          />
                        </Grid>
                      );
                    })}
                    {/* License Plate State/Region */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="License Plate State/Region"
                        {...formik.getFieldProps(
                          `vehicles[${index}].license_plate_state`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Vehicle Specifications
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Battery Capacity */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Battery Capacity"
                        type="number"
                        inputProps={{ step: "any", min: 0 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].battery_capacity`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.battery_capacity &&
                            errors.vehicles?.[index]?.battery_capacity
                        )}
                        helperText={
                          touched.vehicles?.[index]?.battery_capacity &&
                          errors.vehicles?.[index]?.battery_capacity
                        }
                      />
                    </Grid>
                    {/* Motor Type */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          Motor Type
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].motor_type`
                          )}
                          value={formik.values.vehicles[index].motor_type || ""}
                        >
                          <MenuItem value="AC">AC</MenuItem>
                          <MenuItem value="DC">DC</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Seating Capacity */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Seating Capacity"
                        type="number"
                        inputProps={{ min: 1 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].seating_capacity`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.seating_capacity &&
                            errors.vehicles?.[index]?.seating_capacity
                        )}
                        helperText={
                          touched.vehicles?.[index]?.seating_capacity &&
                          errors.vehicles?.[index]?.seating_capacity
                        }
                      />
                    </Grid>
                    {/* Color */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Color"
                        {...formik.getFieldProps(`vehicles[${index}].color`)}
                      />
                    </Grid>
                    {/* Body Type */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          Body Type
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].body_type`
                          )}
                          value={formik.values.vehicles[index].body_type || ""}
                        >
                          <MenuItem value="Sedan">Sedan</MenuItem>
                          <MenuItem value="SUV">SUV</MenuItem>
                          <MenuItem value="Hatchback">Hatchback</MenuItem>
                          <MenuItem value="Truck">Truck</MenuItem>
                          <MenuItem value="Two Wheeler">Two Wheeler</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Owner/Usage Information
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Owner Name */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Owner Name"
                        {...formik.getFieldProps(
                          `vehicles[${index}].owner_name`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.owner_name &&
                            errors.vehicles?.[index]?.owner_name
                        )}
                        helperText={
                          touched.vehicles?.[index]?.owner_name &&
                          errors.vehicles?.[index]?.owner_name
                        }
                      />
                    </Grid>
                    {/* Owner Contact */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Owner Contact"
                        {...formik.getFieldProps(
                          `vehicles[${index}].owner_contact`
                        )}
                      />
                    </Grid>
                    {/* Usage Type */}
                    <Grid item xs={4}>
                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.vehicles?.[index]?.usage_type &&
                            errors.vehicles?.[index]?.usage_type
                        )}
                      >
                        <InputLabel>Usage Type</InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].usage_type`
                          )}
                          value={formik.values.vehicles[index].usage_type || ""}
                        >
                          <MenuItem value="" disabled>
                            Select Usage Type
                          </MenuItem>
                          <MenuItem value="Private">Private</MenuItem>
                          <MenuItem value="Commercial">Commercial</MenuItem>
                        </Select>
                        {touched.vehicles?.[index]?.usage_type &&
                          errors.vehicles?.[index]?.usage_type && (
                            <FormHelperText error>
                              {errors.vehicles[index].usage_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>
                    {/* Odometer Reading */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Odometer Reading"
                        type="number"
                        inputProps={{ min: 0 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].odometer_reading`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.odometer_reading &&
                            errors.vehicles?.[index]?.odometer_reading
                        )}
                        helperText={
                          touched.vehicles?.[index]?.odometer_reading &&
                          errors.vehicles?.[index]?.odometer_reading
                        }
                      />
                    </Grid>
                    {/* Vehicle Insurance Provider */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Vehicle Insurance Provider"
                        {...formik.getFieldProps(
                          `vehicles[${index}].vehicle_insurance_provider`
                        )}
                      />
                    </Grid>
                    {/* Insurance Policy Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Insurance Policy Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].insurance_policy_number`
                        )}
                      />
                    </Grid>
                    {/* Insurance Expiry Date */}
                    <Grid item xs={4}>
                      <FormikDateTimePicker
                        formik={formik}
                        name={`vehicles[${index}].insurance_expiry_date`}
                        label="Insurance Expiry Date"
                        mode="date"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Additional Features/Specifications
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Key Features */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Key Features"
                        multiline
                        rows={4}
                        {...formik.getFieldProps(
                          `vehicles[${index}].key_features`
                        )}
                      />
                    </Grid>
                    {/* Range Per Charge */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Range Per Charge"
                        type="number"
                        inputProps={{ step: "any", min: 0 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].range_per_charge`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.range_per_charge &&
                            errors.vehicles?.[index]?.range_per_charge
                        )}
                        helperText={
                          touched.vehicles?.[index]?.range_per_charge &&
                          errors.vehicles?.[index]?.range_per_charge
                        }
                      />
                    </Grid>
                    {/* Charging Port Type */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          Charging Port Type
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].charging_port_type`
                          )}
                          value={
                            formik.values.vehicles[index].charging_port_type ||
                            ""
                          }
                        >
                          <MenuItem value="CCS2">CCS2</MenuItem>
                          <MenuItem value="Type 2">Type 2</MenuItem>
                          <MenuItem value="CHAdeMO">CHAdeMO</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Top Speed */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Top Speed"
                        type="number"
                        inputProps={{ step: "any", min: 0 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].top_speed`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.top_speed &&
                            errors.vehicles?.[index]?.top_speed
                        )}
                        helperText={
                          touched.vehicles?.[index]?.top_speed &&
                          errors.vehicles?.[index]?.top_speed
                        }
                      />
                    </Grid>
                    {/* Tire Details */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Tire Details"
                        {...formik.getFieldProps(
                          `vehicles[${index}].tire_details`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Service and Maintenance Information
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Last Service Date */}
                    <Grid item xs={4}>
                      <FormikDateTimePicker
                        formik={formik}
                        name={`vehicles[${index}].last_service_date`}
                        label="Last Service Date"
                        mode="date"
                      />
                    </Grid>
                    {/* Service History */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Service History"
                        multiline
                        rows={4}
                        {...formik.getFieldProps(
                          `vehicles[${index}].service_history`
                        )}
                      />
                    </Grid>
                    {/* Next Service Date */}
                    <Grid item xs={4}>
                      <FormikDateTimePicker
                        formik={formik}
                        name={`vehicles[${index}].next_service_date`}
                        label="Next Service Date"
                        mode="date"
                      />
                    </Grid>
                    {/* Warranty Details */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Warranty Details"
                        multiline
                        rows={4}
                        {...formik.getFieldProps(
                          `vehicles[${index}].warranty_details`
                        )}
                      />
                    </Grid>
                    {/* Roadside Assistance Provider */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Roadside Assistance Provider"
                        {...formik.getFieldProps(
                          `vehicles[${index}].roadside_assistance_provider`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">Financials</Typography>
                      <Divider />
                    </Grid>
                    {/* Total Financed Amount */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Total Financed Amount"
                        type="number"
                        inputProps={{ step: "0.01", min: 0 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].total_financed_amount`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.total_financed_amount &&
                            errors.vehicles?.[index]?.total_financed_amount
                        )}
                        helperText={
                          touched.vehicles?.[index]?.total_financed_amount &&
                          errors.vehicles?.[index]?.total_financed_amount
                        }
                      />
                    </Grid>
                    {/* EMI Amount */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="EMI Amount"
                        type="number"
                        inputProps={{ step: "0.01", min: 0 }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].emi_amount`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.emi_amount &&
                            errors.vehicles?.[index]?.emi_amount
                        )}
                        helperText={
                          touched.vehicles?.[index]?.emi_amount &&
                          errors.vehicles?.[index]?.emi_amount
                        }
                      />
                    </Grid>
                    {/* EMI Start Date */}
                    <Grid item xs={4}>
                      <FormikDateTimePicker
                        formik={formik}
                        name={`vehicles[${index}].emi_start_date`}
                        label="EMI Start Date"
                        mode="date"
                      />
                    </Grid>
                    {/* EMI End Date */}
                    <Grid item xs={4}>
                      <FormikDateTimePicker
                        formik={formik}
                        name={`vehicles[${index}].emi_end_date`}
                        label="EMI End Date"
                        mode="date"
                      />
                    </Grid>
                    {/* EMI Cycle */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          EMI Cycle
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].emi_cycle`
                          )}
                          value={formik.values.vehicles[index].emi_cycle || ""}
                        >
                          <MenuItem value="Weekly">Weekly</MenuItem>
                          <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
                          <MenuItem value="Monthly">Monthly</MenuItem>
                          <MenuItem value="Quarterly">Quarterly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Finance Company Name */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Finance Company Name"
                        {...formik.getFieldProps(
                          `vehicles[${index}].finance_company_name`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">Documentation</Typography>
                      <Divider />
                    </Grid>
                    {/* Registration Certificate */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Registration Certificate (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(
                              e,
                              "registration_certificate",
                              index
                            )
                          }
                        />
                      </Button>
                      {vehicle.registration_certificate && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.registration_certificate}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Insurance Document */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Insurance Document (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "insurance_document", index)
                          }
                        />
                      </Button>
                      {vehicle.insurance_document && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.insurance_document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Pollution Certificate */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Pollution Certificate (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "pollution_certificate", index)
                          }
                        />
                      </Button>
                      {vehicle.pollution_certificate && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.pollution_certificate}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Invoice Document */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Invoice Document (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "invoice_document", index)
                          }
                        />
                      </Button>
                      {vehicle.invoice_document && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.invoice_document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Loan/Lease Details */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Loan/Lease Details (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "loan_lease_details", index)
                          }
                        />
                      </Button>
                      {vehicle.loan_lease_details && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.loan_lease_details}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>

                    {/* Save/Update/Remove Buttons */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={isSubmitting}
                        >
                          Save
                        </LoadingButton>
                        <LoadingButton
                          size="large"
                          type="button"
                          variant="outlined"
                          component={RouterLink}
                          to={backPath}
                        >
                          Cancel
                        </LoadingButton>
                      </Stack>
                    </Grid>
                  </Grid>
                ))}
              </>
            )}
          />
        </Form>
      </FormikProvider>
    </Card>
  );

  // New vehicle — no tabs needed, just the form
  if (!id) {
    return formBody;
  }

  // Existing vehicle — tabbed layout: Vehicle Details | Charging History
  return (
    <TabContext value={selectedTab}>
      <Box
        sx={{
          mb: 3,
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "background.default",
          py: 1,
        }}
      >
        <TabList
          onChange={(e, tab) => setSelectedTab(tab)}
          sx={{
            minHeight: 44,
            width: "fit-content",
            bgcolor: (theme) => alpha(theme.palette.grey[300], 0.5),
            borderRadius: 2,
            p: 0.5,
            "& .MuiTabs-flexContainer": { gap: 0.5 },
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          {[
            { value: "details",          label: "Vehicle Details"   },
            { value: "charging-history", label: "Charging History"  },
          ].map(({ value, label }) => (
            <Tab
              key={value}
              value={value}
              label={label}
              sx={{
                minHeight: 36,
                px: 2.5,
                py: 0.75,
                borderRadius: 1.5,
                fontSize: "0.875rem",
                fontWeight: 600,
                textTransform: "none",
                letterSpacing: 0.3,
                color: "text.secondary",
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  color: "white",
                  bgcolor: "primary.main",
                  boxShadow: (theme) =>
                    `0 3px 10px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
                "&:hover:not(.Mui-selected)": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                },
              }}
            />
          ))}
        </TabList>
      </Box>

      <TabPanel value="details" sx={{ p: 0 }}>
        {formBody}
      </TabPanel>

      <TabPanel value="charging-history" sx={{ p: 0 }}>
        <VehicleChargingHistory
          vehicleId={id}
          hasBatteryMacs={Boolean(
            formik.values.vehicles?.[0]?.battery_mac_1 || formik.values.vehicles?.[0]?.battery_mac_2
          )}
          batteryCapacity={formik.values.vehicles?.[0]?.battery_capacity}
        />
      </TabPanel>
    </TabContext>
  );
};

export default VehicleDetailsForm;
